import cs from "classnames";
import React from "react";
import { GoChevronDown, GoChevronUp } from "react-icons/go";

import { useOnClickOutside } from "src/common/hooks/useOnClickOutside";

import "./DropdownMenu.css";

interface DropdownMenuProps {
	label: React.ReactNode;
}

export const DropdownMenu: React.FunctionComponent<
	React.PropsWithChildren<DropdownMenuProps>
> = ({ label, children }) => {
	const [isOpen, setOpen] = React.useState(false);

	const containerRef = React.useRef<HTMLDivElement>(null);
	const contentRef = React.useRef<HTMLDivElement>(null);

	const onToggle = React.useCallback(() => {
		setOpen(isOpen => !isOpen);
	}, []);

	const onClose = React.useCallback(() => {
		setOpen(false);
	}, []);

	useOnClickOutside(containerRef, onClose);

	React.useEffect(() => {
		if (!isOpen) {
			return;
		}

		const { current: container } = containerRef;
		if (container === null) {
			return;
		}

		const { current: content } = contentRef;
		if (content === null) {
			return;
		}

		const containerRect = container.getBoundingClientRect();
		const contentRect = content.getBoundingClientRect();

		const topMin = 10;
		const leftMin = 10;

		const top = Math.round(containerRect.top + containerRect.height) + 5;
		const left = Math.round(containerRect.left) - 20;

		const topMax = window.innerHeight - contentRect.height - 10;
		const leftMax = window.innerWidth - contentRect.width - 10;

		content.style.top = `${Math.max(Math.min(top, topMax), topMin)}px`;
		content.style.left = `${Math.max(Math.min(left, leftMax), leftMin)}px`;
	}, [isOpen]);

	return (
		<div
			ref={containerRef}
			className={cs("dropdown-menu", { "dropdown-menu--open": isOpen })}
		>
			<div className="dropdown-menu__label" onClick={onToggle}>
				{label}
				{isOpen ? <GoChevronUp /> : <GoChevronDown />}
			</div>
			<div
				ref={contentRef}
				className="dropdown-menu__content"
				onClick={onClose}
			>
				{children}
			</div>
		</div>
	);
};

interface DropdownMenuItemProps {
	onClick?: (event: React.MouseEvent) => unknown;
}

export const DropdownMenuItem: React.FunctionComponent<
	React.PropsWithChildren<DropdownMenuItemProps>
> = ({ onClick, children }) => (
	<div className="dropdown-menu__item" onClick={onClick}>
		{children}
	</div>
);
